/**
 * swiperNav — wires prev/next arrow buttons to a horizontally-scrolling track.
 *
 * Clicking pages the track by one viewport width (smooth; instant under
 * prefers-reduced-motion). Both arrows are hidden when the track has nothing to
 * scroll (no overflow / empty); when scrollable, the unusable arrow at each end
 * is disabled. All listeners are bound with { signal } so the host component's
 * AbortController tears them down on disconnect.
 *
 * @param {HTMLElement} track            the scroll container
 * @param {{prev: HTMLButtonElement|null, next: HTMLButtonElement|null}} buttons
 * @param {AbortSignal} signal
 * @returns {() => void} update — re-evaluates arrow visibility/disabled state;
 *   call after the track's contents change (e.g. async-loaded items).
 */
function swiperNav(track, { prev, next }, signal) {
  if (!track) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  let raf = null;

  const go = (direction) => {
    track.scrollBy({
      left: direction * track.clientWidth,
      behavior: reduce.matches ? 'auto' : 'smooth',
    });
  };

  const update = () => {
    const maxScroll = track.scrollWidth - track.clientWidth;
    const overflowing = maxScroll > 1;
    if (prev) {
      prev.hidden = !overflowing;
      prev.disabled = track.scrollLeft <= 0;
    }
    if (next) {
      next.hidden = !overflowing;
      next.disabled = track.scrollLeft >= maxScroll - 1;
    }
  };

  const onScroll = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      update();
      raf = null;
    });
  };

  prev?.addEventListener('click', () => go(-1), { signal });
  next?.addEventListener('click', () => go(1), { signal });
  track.addEventListener('scroll', onScroll, { passive: true, signal });
  window.addEventListener('resize', onScroll, { passive: true, signal });

  update();

  return update;
}

window.swiperNav = swiperNav;
